import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Sparkles, Check, ArrowRight, Wand2, Eraser, Maximize2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import TermsCheckbox from "@/components/TermsCheckbox";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_makelaar-booking/artifacts/c00a1vjs_Logo%20Social%20Factory%20werkbestand_Tekengebied%201-02.png";
const BEFORE = "https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/ztuzvzpa_image-1790454810950.jpg";
const AFTER = "https://customer-assets-7cd3h4nn.emergentagent.net/job_makelaar-booking/artifacts/vcc33jsw_image-1790454813576.jpg";

const T = {
    nl: {
        login: "Al een account? Inloggen", eyebrow: "Voor vastgoedmakelaars",
        h1a: "Bewerk je vastgoedfoto's met AI.", h1b: "Klaar in 1 minuut.",
        sub: "Upload een foto van je pand en laat AI hem verkoopklaar maken. Geen fotograaf nodig, geen software te leren.",
        usps: ["Foto's verbeteren — scherper, lichter, strakkere kleuren", "Storende objecten wissen — vuilbakken, kabels, auto's", "Virtuele inrichting — lege ruimtes smaakvol gemeubeld"],
        slider_hint: "↔ Sleep de schuif: echte bewerking uit onze AI Foto Retouche tool",
        badge: "🎁 10 gratis credits — geen betaalkaart nodig", form_title: "Start gratis",
        form_sub: "Account in 30 seconden, meteen foto's bewerken.",
        name: "Naam", name_ph: "Jan Janssens", email: "E-mail", email_ph: "jan@kantoor.be",
        password: "Wachtwoord", password_ph: "Minstens 8 tekens",
        submit: "Maak mijn gratis account", busy: "Account aanmaken…",
        chips: ["Geen abonnement", "Direct starten", "GDPR-conform"],
        terms_err: "Aanvaard eerst de algemene voorwaarden",
        success: "Account klaar! Je 10 gratis credits staan al op je account 🎁",
        pending: "Account aangevraagd — je hoort snel van ons!",
        logged_in: "Je bent al ingelogd — ga meteen aan de slag.", open_tools: "Open de AI-fototools",
    },
    fr: {
        login: "Déjà un compte ? Se connecter", eyebrow: "Pour agents immobiliers",
        h1a: "Retouchez vos photos immobilières avec l'IA.", h1b: "Prêt en 1 minute.",
        sub: "Téléchargez une photo de votre bien et laissez l'IA la rendre prête à vendre. Pas de photographe, pas de logiciel à apprendre.",
        usps: ["Améliorer les photos — plus nettes, plus lumineuses", "Effacer les objets gênants — poubelles, câbles, voitures", "Meublage virtuel — des pièces vides joliment aménagées"],
        slider_hint: "↔ Glissez le curseur : vraie retouche de notre outil IA",
        badge: "🎁 10 crédits gratuits — sans carte bancaire", form_title: "Commencez gratuitement",
        form_sub: "Compte en 30 secondes, retouchez vos photos immédiatement.",
        name: "Nom", name_ph: "Jean Dupont", email: "E-mail", email_ph: "jean@agence.be",
        password: "Mot de passe", password_ph: "Minimum 8 caractères",
        submit: "Créer mon compte gratuit", busy: "Création du compte…",
        chips: ["Sans abonnement", "Démarrage immédiat", "Conforme RGPD"],
        terms_err: "Veuillez d'abord accepter les conditions générales",
        success: "Compte prêt ! Vos 10 crédits gratuits sont déjà disponibles 🎁",
        pending: "Compte demandé — nous revenons vite vers vous !",
        logged_in: "Vous êtes déjà connecté — lancez-vous.", open_tools: "Ouvrir les outils photo IA",
    },
    en: {
        login: "Already have an account? Log in", eyebrow: "For real estate agents",
        h1a: "Edit your property photos with AI.", h1b: "Done in 1 minute.",
        sub: "Upload a photo of your property and let AI make it sale-ready. No photographer needed, no software to learn.",
        usps: ["Enhance photos — sharper, brighter, better colours", "Remove distracting objects — bins, cables, cars", "Virtual staging — empty rooms tastefully furnished"],
        slider_hint: "↔ Drag the slider: real edit from our AI Photo Retouch tool",
        badge: "🎁 10 free credits — no credit card needed", form_title: "Start for free",
        form_sub: "Account in 30 seconds, start editing right away.",
        name: "Name", name_ph: "John Smith", email: "Email", email_ph: "john@agency.com",
        password: "Password", password_ph: "At least 8 characters",
        submit: "Create my free account", busy: "Creating account…",
        chips: ["No subscription", "Start instantly", "GDPR compliant"],
        terms_err: "Please accept the terms & conditions first",
        success: "Account ready! Your 10 free credits are waiting 🎁",
        pending: "Account requested — we'll get back to you soon!",
        logged_in: "You're already logged in — jump right in.", open_tools: "Open the AI photo tools",
    },
    es: {
        login: "¿Ya tienes cuenta? Inicia sesión", eyebrow: "Para agentes inmobiliarios",
        h1a: "Edita tus fotos inmobiliarias con IA.", h1b: "Listo en 1 minuto.",
        sub: "Sube una foto de tu inmueble y deja que la IA la prepare para la venta. Sin fotógrafo, sin software que aprender.",
        usps: ["Mejorar fotos — más nítidas y luminosas", "Eliminar objetos molestos — papeleras, cables, coches", "Amueblado virtual — estancias vacías decoradas con gusto"],
        slider_hint: "↔ Desliza: edición real de nuestra herramienta de retoque IA",
        badge: "🎁 10 créditos gratis — sin tarjeta", form_title: "Empieza gratis",
        form_sub: "Cuenta en 30 segundos, edita fotos al instante.",
        name: "Nombre", name_ph: "Juan Pérez", email: "Correo", email_ph: "juan@agencia.es",
        password: "Contraseña", password_ph: "Mínimo 8 caracteres",
        submit: "Crear mi cuenta gratis", busy: "Creando cuenta…",
        chips: ["Sin suscripción", "Empieza al momento", "Cumple RGPD"],
        terms_err: "Acepta primero los términos y condiciones",
        success: "¡Cuenta lista! Tus 10 créditos gratis te esperan 🎁",
        pending: "Cuenta solicitada — ¡te contactamos pronto!",
        logged_in: "Ya has iniciado sesión — empieza ahora.", open_tools: "Abrir las herramientas IA",
    },
};

export default function TryLanding() {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lang = (i18n.language || "nl").slice(0, 2);
    const t = T[lang] || T.nl;
    const { register, user } = useAuth();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [accepted, setAccepted] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!accepted) {
            toast.error(t.terms_err);
            return;
        }
        setBusy(true);
        let signupUtm = null;
        try { signupUtm = JSON.parse(localStorage.getItem("signup_utm") || "null"); } catch (err) { /* noop */ }
        const res = await register({ ...form, accepted_terms: true, preferred_language: lang, signup_utm: signupUtm });
        setBusy(false);
        if (!res.ok) {
            toast.error(res.error);
            return;
        }
        try { window.oaiq?.("measure", "registration_completed", { type: "customer_action" }); } catch (err) { /* noop */ }
        if (res.pending) {
            toast.success(res.message || t.pending);
            return;
        }
        toast.success(t.success);
        navigate("/tools");
    };

    if (user) {
        return (
            <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
                <div className="text-center">
                    <p className="text-zinc-600 mb-4">{t.logged_in}</p>
                    <Button onClick={() => navigate("/tools")} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm" data-testid="try-goto-tools">
                        {t.open_tools} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="try-landing-page">
            <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
                <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="try-logo">
                    <img src={LOGO} alt="Social Factory" className="h-14 w-14 md:h-16 md:w-16 object-contain invert" />
                    <span className="hidden sm:block text-base font-bold tracking-tight">Vastgoed Studio Pro</span>
                </Link>
                <div className="flex items-center gap-4">
                    <LanguageSwitcher variant="compact" />
                    <Link to="/login" className="text-sm text-zinc-500 hover:text-zinc-950 whitespace-nowrap" data-testid="try-login-link">{t.login}</Link>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 pt-8 pb-20 grid lg:grid-cols-2 gap-12 items-start">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C8922C] font-bold mb-5">
                        <Sparkles className="h-3.5 w-3.5" /> {t.eyebrow}
                    </div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-5">
                        {t.h1a}<br />
                        <span className="text-[#C8922C]">{t.h1b}</span>
                    </h1>
                    <p className="text-zinc-500 text-base leading-relaxed mb-8 max-w-lg">{t.sub}</p>
                    <div className="space-y-3 mb-10">
                        {[Wand2, Eraser, Maximize2].map((Icon, i) => (
                            <div key={i} className="flex items-center gap-3 text-sm text-zinc-700">
                                <span className="h-8 w-8 flex items-center justify-center rounded-sm bg-[#E5A93D]/15 text-[#C8922C] flex-shrink-0"><Icon className="h-4 w-4" /></span>
                                {t.usps[i]}
                            </div>
                        ))}
                    </div>
                    <div className="rounded-sm overflow-hidden border border-zinc-200 shadow-sm">
                        <BeforeAfterSlider beforeUrl={BEFORE} afterUrl={AFTER} testId="try-before-after" />
                    </div>
                    <p className="text-xs text-zinc-400 mt-3">{t.slider_hint}</p>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
                    className="bg-white border border-zinc-200 rounded-md p-7 lg:p-9 lg:sticky lg:top-8 shadow-sm">
                    <div className="inline-flex items-center gap-2 bg-[#E5A93D]/15 text-[#C8922C] text-xs font-bold px-3 py-1.5 rounded-sm mb-5">
                        {t.badge}
                    </div>
                    <h2 className="font-display font-black text-2xl tracking-tight mb-1">{t.form_title}</h2>
                    <p className="text-sm text-zinc-500 mb-6">{t.form_sub}</p>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">{t.name}</label>
                            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder={t.name_ph} className="mt-1 bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="try-name" />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">{t.email}</label>
                            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder={t.email_ph} className="mt-1 bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="try-email" />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">{t.password}</label>
                            <Input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                placeholder={t.password_ph} className="mt-1 bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="try-password" />
                        </div>
                        <TermsCheckbox checked={accepted} onChange={setAccepted} />
                        <Button type="submit" disabled={busy}
                            className="w-full h-12 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                            data-testid="try-submit">
                            {busy ? t.busy : t.submit} <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </form>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5 text-[11px] text-zinc-400">
                        <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-500" /> {t.chips[0]}</span>
                        <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-500" /> {t.chips[1]}</span>
                        <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> {t.chips[2]}</span>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
